"use client"

import { useMemo } from "react"

import { useFetchCurrentUser } from "@/hooks/api/users"
import { useDevAuth } from "@/hooks/use-dev-auth"

type SessionMetadata = {
  role?: unknown
  engineer?: unknown
}

type SessionClaims = {
  metadata?: SessionMetadata
  commissioner?: unknown
  engineer?: unknown
}

type AuthRoles = {
  isCommissioner: boolean
  isEngineer: boolean
}

interface UseAppAuthStateOptions {
  includeCurrentUser?: boolean
}

const defaultRoles: AuthRoles = {
  isCommissioner: false,
  isEngineer: false,
}

function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "string") return value.toLowerCase() === "true"
  return false
}

function deriveRolesFromClaims(claims: SessionClaims | null | undefined): AuthRoles {
  if (!claims) return defaultRoles

  const metadata = claims.metadata ?? {}

  return {
    isCommissioner: metadata.role === "commissioner" || toBoolean(claims.commissioner),
    isEngineer: toBoolean(metadata.engineer) || toBoolean(claims.engineer),
  }
}

export function useAppAuthState(options: UseAppAuthStateOptions = {}) {
  const includeCurrentUser = options.includeCurrentUser ?? false
  const { isLoaded, isSignedIn, userId, sessionClaims } = useDevAuth()
  const shouldFetchCurrentUser = includeCurrentUser && isLoaded && Boolean(isSignedIn)
  const { data: currentUser, isLoading: isCurrentUserLoading } = useFetchCurrentUser({
    enabled: shouldFetchCurrentUser,
  })

  const roles = useMemo(
    () => deriveRolesFromClaims(isSignedIn ? (sessionClaims as SessionClaims | null | undefined) : null),
    [isSignedIn, sessionClaims]
  )

  const isLoading = useMemo(() => {
    return !isLoaded || (includeCurrentUser && isCurrentUserLoading)
  }, [isLoaded, includeCurrentUser, isCurrentUserLoading])

  return {
    isSignedIn: Boolean(isSignedIn),
    clerkUserId: userId ?? null,
    currentUser: includeCurrentUser ? (currentUser ?? null) : null,
    isCommissioner: roles.isCommissioner,
    isEngineer: roles.isEngineer,
    isLoading,
  }
}
